import { z } from 'zod';

import { resolveUnitToBaseRate } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitToBaseRate';
import { maxIssueQtyInUnit } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueQtyBounds';

function isPositiveInteger(value) {
  if (value === '' || value == null) {
    return false;
  }
  const n = Number(value);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 1;
}

const lineSchema = z.object({
  invStockId: z.string().min(1, '請選擇來源庫存'),
  stockNo: z.string().optional().nullable(),
  mtrId: z.string().optional().nullable(),
  mtrNo: z.string().optional().nullable(),
  mtrName: z.string().optional().nullable(),
  fromWarehouseId: z.string().optional().nullable(),
  fromWarehouseLabel: z.string().optional().nullable(),
  fromLocationLabel: z.string().optional().nullable(),
  /** 基本單位可用量（非所選 unit 語意） */
  availableQty: z.union([z.string(), z.number()]).optional().nullable(),
  /** 可選；空則後端用物料 base */
  unitId: z.string().optional().nullable(),
  unitOptions: z.array(z.any()).optional().nullable(),
  qty: z.union([z.string(), z.number(), z.null()]),
  toLocationId: z.string().optional().nullable(),
  stockNoPolicy: z.enum(['REMAINING_KEEP_OLD', 'TRANSFERRED_KEEP_OLD']),
  remark: z.string().optional().nullable(),
}).superRefine((line, ctx) => {
  const qty = line.qty;
  if (!isPositiveInteger(qty)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '轉倉數量須為 ≥ 1 的整數',
      path: ['qty'],
    });
    return;
  }

  const n = Number(qty);
  const available = Number(line.availableQty);
  if (!Number.isFinite(available) || available <= 0) {
    return;
  }

  const maxQty = maxIssueQtyInUnit(available, line.unitOptions, line.unitId);
  if (n > maxQty) {
    const rate = resolveUnitToBaseRate(line.unitOptions, line.unitId) ?? 1;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: rate === 1
        ? `轉倉數量不可超過可用量（基本單位 ${available}）`
        : `轉倉數量不可超過本單位上限（${maxQty}；基本單位可用 ${available}）`,
      path: ['qty'],
    });
  }
});

export const stockTransferFormSchema = z.object({
  transferDate: z.string().min(1, '請填寫轉倉日'),
  toWarehouseId: z.string().min(1, '請選擇轉倉倉別'),
  sourceType: z.enum(['MANUAL', 'STOCK_REQUEST']).default('MANUAL'),
  sourceId: z.string().optional().nullable(),
  issueReason: z.string().optional().nullable(),
  remark: z.string().optional().nullable(),
  lines: z.array(lineSchema).min(1, '至少選擇一筆庫存'),
});

/** 工單轉倉：略過 IMS sourceType enum（後端固定 WORK_ORDER，不當作一般轉倉選項）。 */
export const workOrderStockTransferFormSchema = stockTransferFormSchema.omit({
  sourceType: true,
  sourceId: true,
});

export const STOCK_TRANSFER_FIELD_LABELS = {
  transferDate: '轉倉日',
  toWarehouseId: '轉倉倉別',
  sourceType: '來源類型',
  sourceId: '來源單號',
  issueReason: '事由',
  remark: '備註',
  lines: '轉倉明細',
  'lines.invStockId': '來源庫存',
  'lines.unitId': '單位',
  'lines.qty': '轉倉數量',
  'lines.toLocationId': '目標儲位（可選）',
  'lines.stockNoPolicy': '庫編設定',
  'lines.remark': '行備註',
};

export const TO_WAREHOUSE_TIP = '請先選擇轉倉倉別後再選庫存';
